"use client";

import { useEffect, useRef, useState } from "react";
import { TComponentProps } from "@/types";
import { tv, VariantProps } from "tailwind-variants";

/**
 * This component is a scroll container with feathered edges that are
 * automatically generated based on the containers scroll position.
 *
 * i.e. if there is a horizontal overflow, the right hand side will have
 * a feathered edge until the container is scrolled to the end. If it has
 * been scrolled at all from the left, it will also have a feathered edge
 * on the left hand side.
 *
 * The component also supports vertical overflow using the scrollDirection
 * prop - the same logic applies, but the feathered edges are on the top
 * and bottom.
 *
 * @param scrollDirection - The direction of the scroll container (horizontal (default) or vertical)
 * @param scrollbar - Whether to show a scrollbar (false by default)
 * @param children - The children of the scroll container.
 * @param className - The className of the scroll container.
 */

const variants = tv({
  base: "",
  variants: {
    scrollDirection: {
      horizontal: "w-full overflow-x-auto",
      vertical: "h-full overflow-y-auto",
    },
    fadeLeft: {
      true: "[mask:linear-gradient(to_right,transparent,#000_15%)]",
      false: "",
    },
    fadeRight: {
      true: "[mask:linear-gradient(to_left,transparent,#000_15%)]",
      false: "",
    },
    fadeTop: {
      true: "[mask:linear-gradient(to_bottom,transparent,#000_15%)]",
      false: "",
    },
    fadeBottom: {
      true: "[mask:linear-gradient(to_top,transparent,#000_15%)]",
      false: "",
    },

    scrollbar: {
      true: "",
      false: "no-scrollbar",
    },
  },
  defaultVariants: {
    scrollDirection: "horizontal",
    scrollbar: false,
  },
  compoundVariants: [
    {
      scrollDirection: "horizontal",
      fadeLeft: true,
      fadeRight: true,
      className:
        "[mask:linear-gradient(to_right,transparent,#000_15%,#000_85%,transparent)]",
    },
    {
      scrollDirection: "vertical",
      fadeTop: true,
      fadeBottom: true,
      className:
        "[mask:linear-gradient(to_bottom,transparent,#000_15%,#000_85%,transparent)]",
    },
  ],
});

type FeatheredScrollContainerProps = VariantProps<typeof variants> &
  TComponentProps & {
    scrollDirection?: "horizontal" | "vertical";
    scrollbar?: boolean;
  };

export const FeatheredScrollContainer = ({
  scrollDirection = "horizontal",
  scrollbar = false,
  children,
  className,
}: FeatheredScrollContainerProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const [scrollFromStart, setScrollFromStart] = useState<number>(0);
  const [scrolledToEnd, setScrolledToEnd] = useState<boolean>(false);

  useEffect(() => {
    const container = ref.current;
    const handleScroll = () => {
      if (container) {
        const {
          scrollLeft,
          scrollTop,
          scrollWidth,
          scrollHeight,
          clientWidth,
          clientHeight,
        } = container;

        // If the scroll direction is horizontal, we use scrollLeft and scrollWidth
        if (scrollDirection === "horizontal") {
          setScrollFromStart(scrollLeft);
          setScrolledToEnd(
            scrollWidth <= clientWidth ||
              scrollLeft + clientWidth >= scrollWidth,
          );
        } else {
          // If the scroll direction is vertical, we use scrollTop and scrollHeight
          setScrollFromStart(scrollTop);
          setScrolledToEnd(
            scrollHeight <= clientHeight ||
              scrollTop + clientHeight >= scrollHeight,
          );
        }
      }
    };

    // Add both scroll and resize listeners
    container?.addEventListener("scroll", handleScroll);
    window.addEventListener("resize", handleScroll);

    // Initial check
    handleScroll();

    return () => {
      // Clean up both listeners
      container?.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleScroll);
    };
  }, [scrollDirection]);

  return (
    <div
      ref={ref}
      className={variants({
        fadeLeft: scrollDirection === "horizontal" && scrollFromStart > 0,
        fadeRight: scrollDirection === "horizontal" && !scrolledToEnd,
        fadeTop: scrollDirection === "vertical" && scrollFromStart > 0,
        fadeBottom: scrollDirection === "vertical" && !scrolledToEnd,
        scrollDirection,
        scrollbar,
        className,
      })}
    >
      {children}
    </div>
  );
};
